import {
  ChevronLeft,
  GripHorizontal,
  PictureInPicture2,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  CANVAS_GLYPH_STROKE,
  CANVAS_MENU_GLYPH,
} from "../canvasToolbarComposition";
import { TrayPortraitProvider } from "./CanvasTrayContent";
import "./canvasTray.css";
import { focusedEditableElement } from "./emojiInsert";

/**
 * The tray — the one disclosure surface the bar's picker tiles share
 * (exp-canvas-toolbar tray.jsx). Content-agnostic: the frame is the same
 * object for every tool — 402 wide, centred on the bar, tucked 8px behind
 * it, a 24px chrome row standing outside the panel on the canvas — and only
 * what fills it changes. Switching tools cross-fades the content inside a
 * frame that never leaves; the same trigger closes it.
 *
 * States: open · popped (draggable, a non-modal dialog) · closed. The
 * sheet's third state, minimised, is gone (ROUND5 §4, D49). The frame
 * measures its content and writes the panel height back as `--xtt-h` (the
 * rise travels an absolute distance), the room above the bar as
 * `--xtt-avail`, and the pinned head and foot heights the deck clears
 * itself by.
 *
 * Parent contract: render inside the positioned mount whose box is the
 * bar's box, before the bar in the DOM. Beside the fit shed's vertical
 * column (`beside`) the frame stands to the column's right, its bottom on
 * the column's foot, over the chat, in portrait: its long side runs up the
 * column, its height capped at the column's and clamped to the band's.
 * Popped, it keeps that shape, so a pop never jumps; it docks back portrait.
 */

export type CanvasTrayState = "open" | "popped" | "closed";
export type CanvasTrayCloseReason = "escape" | "outside" | "chrome" | "bar";

export const TRAY_TUCK = 8;
const TRAY_CHROME_H = 24;
const TRAY_GAP = 4;
/** A pointer that travels less than this is the click it always was. */
export const TRAY_DRAG_THRESHOLD = 4;
/**
 * Where a pop lands: visibly clear of the bar — up by more than the chrome
 * row (24) and sideways by more than a tile (40), snapped to the space
 * ladder: --space-12 right, --space-8 up (ROUND3 §1, D37). Mirrors only.
 */
export const TRAY_POP_DX = 48;
export const TRAY_POP_DY = 32;
/** A docked clip's air on the rail side — the row's own inset (D53). */
const TRAY_DOCK_INSET = 16;

/**
 * Where each tray was last left while popped, for the life of the page —
 * the panel's top-left relative to the canvas, so the seat survives a change
 * of height or anchor between pops (ROUND5 §7) and stays where it was left
 * whatever the bar has done since (ROUND7).
 */
const poppedSeats = new Map<string, { left: number; top: number }>();

/**
 * Popped, the clip is anchored at its top so a body that changes height
 * grows from the top-left (ROUND5 §7, D52); docked, it hangs from the bar's
 * top edge. This is the docked clip's top relative to the mount — the
 * target a dock travels to while the top anchor is still in force.
 */
export function dockedClipTop(clipHeight: number, wide = false): number {
  return dockedClipBottom(wide) - clipHeight;
}

/**
 * Where a docked clip's bottom sits, below the bar's top edge: tucked 8
 * behind the bar, or, for a body wider than the bar, standing --space-1
 * clear above it (D51). The one source for the dock target; canvasTray.css
 * draws the same two seats (its test pins them).
 */
export const TRAY_WIDE_STANDOFF = 4;
export function dockedClipBottom(wide: boolean): number {
  return wide ? -TRAY_WIDE_STANDOFF : TRAY_TUCK;
}

/**
 * A docked clip centres on the bar; a body wider than the bar can cross
 * the chat when the bar stands against it (ROUND6, D54). Slide it left by
 * exactly the overshoot past the bounds' right edge and no more, and never
 * inside the rail-side inset — which can mean standing right of centre
 * when the body is wider than the room beside the bar.
 */
export function dockedClipShift({
  centre,
  halfWidth,
  boundsLeft,
  boundsRight,
  inset,
}: {
  centre: number;
  halfWidth: number;
  boundsLeft: number;
  boundsRight: number;
  inset: number;
}): number {
  const overshoot = Math.max(0, centre + halfWidth - boundsRight);
  const floor = boundsLeft + inset - (centre - halfWidth);
  // `|| 0` keeps a -0 from reaching the style.
  return Math.max(-overshoot, floor) || 0;
}

// Mirrors of --dur-fast / --dur-mid / --dur-slow. Timers only.
const D_FAST = 100;
const D_MID = 200;
const D_SLOW = 300;

const MOVE_LIFT = "var(--dur-mid)";
const MOVE_DOCK = "var(--dur-slow)";

/**
 * What a pointer-down inside the popped panel must leave alone: fields, the
 * HTML5-draggable plates, and dnd-kit's draggables (the entity picker's
 * rows carry `aria-roledescription="draggable"`), which drag onto the
 * canvas and must win over the panel (ROUND5 §5, D50).
 */
export const PANEL_DRAG_EXCLUDED =
  'input, textarea, select, [contenteditable="true"], [draggable="true"], ' +
  '[aria-roledescription="draggable"]';

const FOCUSABLE =
  "[data-tray-autofocus], button:not([disabled]), [href], input:not([disabled]), " +
  'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type Phase = "gone" | "entering" | "idle" | "leaving" | "falling";

const clamp = (v: number, lo: number, hi: number) =>
  hi < lo ? lo : v < lo ? lo : v > hi ? hi : v;

/** The row's slide (transition-[left] duration-200) plus a frame to land. */
const ROW_SLIDE_SETTLE_MS = 240;

/**
 * The deck's ceiling, in px: the room above the clip's bottom less the
 * chrome row, its gap and the tuck. Beside the column there is no tuck,
 * and docked there the clip is also no taller than the column (`column`).
 */
export function deckAvail({
  room,
  column,
  beside,
}: {
  room: number;
  column?: number;
  beside: boolean;
}): number {
  const tall = column && column > 0 ? Math.min(room, column) : room;
  const lift = TRAY_CHROME_H + TRAY_GAP + (beside ? 0 : TRAY_TUCK);
  return Math.max(0, Math.round(tall - lift));
}

/** Air a popped panel keeps off the chat's painted edge: the toolbar's (D55). */
export const TRAY_CHAT_AIR = 16;

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * How far left a popped panel must stand to clear the chat (ROUND7): zero
 * unless the panel, with the air, overlaps the chat both across and down,
 * and then exactly the overshoot. Above or below the chat the panel is free.
 */
export function chatPushX(
  panel: Box,
  chat: Box | null,
  air = TRAY_CHAT_AIR,
): number {
  if (!chat) return 0;
  const overlapsY =
    panel.top < chat.bottom + air && panel.bottom > chat.top - air;
  const overshoot = panel.right - (chat.left - air);
  return overlapsY && overshoot > 0 ? -overshoot : 0;
}

/** The floating chat on the right: its window, or its button. */
function chatEl(): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    '[data-floating-chat="window"], [data-floating-chat="fab"]',
  );
}
function chatBox(): Box | null {
  const r = chatEl()?.getBoundingClientRect();
  return r && r.width > 0 ? r : null;
}

/**
 * A popped panel roams the whole canvas (ROUND7): the container the band
 * sits in, not the band, which stops at the chat's edge all the way up.
 * Null when nothing measurable holds it.
 */
function canvasBox(stage: HTMLElement | null): Box | null {
  const host = (stage?.offsetParent as HTMLElement | null) ?? stage;
  const r = host?.getBoundingClientRect();
  return r && r.width > 0 ? r : null;
}

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduce(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return reduce;
}

interface CanvasTrayProps {
  title: string;
  state: CanvasTrayState;
  onState: (next: CanvasTrayState, reason: CanvasTrayCloseReason) => void;
  children: ReactNode;
  /** Clicks here never count as outside — another tile swaps or closes. */
  ignoreSelector?: string;
  /** Popped, the panel stays inside this element; the viewport otherwise. */
  boundsSelector?: string;
  /** Re-popping the same key returns to where the user left it. */
  memoryKey?: string;
  /**
   * A drilled-in body (ROUND3 §2): the chrome names it with its mark and a
   * back control; Escape inside backs out before it closes.
   */
  head?: {
    lead: ReactNode;
    label: string;
    backLabel: string;
    onBack: () => void;
  };
  /** The body owns its width: the picker's, not the roster's. */
  wide?: boolean;
  /** Changes when the bar moves, so a docked clip re-measures its bounds. */
  reflowKey?: number;
  /**
   * The bar's left in the band. A popped panel cancels the bar's travel so
   * it stays where it was left when the bar slides (ROUND7).
   */
  barLeft?: number;
  /** The bar's slide is eased (not while a chat resize drags it 1:1). */
  barEases?: boolean;
  /**
   * Beside the vertical column, which paints over the chat: no dock clamp
   * to the band and no push off the chat, either of which would shove the
   * panel back over the column. The frame and its body go portrait.
   */
  beside?: boolean;
}

export function CanvasTray({
  title,
  state,
  onState,
  children,
  ignoreSelector = '[role="toolbar"]',
  boundsSelector = "[data-tray-bounds]",
  memoryKey,
  head,
  wide = false,
  reflowKey,
  barLeft,
  barEases = true,
  beside = false,
}: CanvasTrayProps) {
  const reduce = usePrefersReducedMotion();

  const rootRef = useRef<HTMLDivElement>(null);
  const wellRef = useRef<HTMLDivElement>(null);
  const phaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const swapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drag = useRef<{
    pointerId: number;
    px: number;
    py: number;
    ox: number;
    oy: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    /** The clip's box at the press, before the offset. */
    rect: Box;
    /** The chat the carried panel keeps clear of (ROUND7). */
    chat: Box | null;
    /** Past the threshold: the panel is being carried. */
    carrying: boolean;
  } | null>(null);

  const titleId = useId();
  const regionId = useId();

  const current = state;

  // The state the frame is drawn in lags `current` through a close so the
  // tray animates out of the state it was in.
  const [viewState, setViewState] = useState<
    Exclude<CanvasTrayState, "closed">
  >(current === "closed" ? "open" : current);
  const [phase, setPhase] = useState<Phase>(() =>
    current === "closed" ? "gone" : "entering",
  );
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [floating, setFloating] = useState(current === "popped");
  /** The clip's inline top while popped; null hangs it from the bar again. */
  const [anchorTop, setAnchorTop] = useState<number | null>(null);
  /** The docked top-left the offset is measured from, taken at the pop. */
  const base = useRef({ left: 0, top: 0 });
  /** A dock whose target waits for the docked measurement. */
  const dockPending = useRef(false);
  const [moveDur, setMoveDur] = useState(MOVE_LIFT);
  /** The bar's left when this panel popped; null while docked. */
  const [pinFrom, setPinFrom] = useState<number | null>(null);
  /** How far left the chat is holding a popped panel right now (ROUND7). */
  const [push, setPush] = useState(0);
  /**
   * Where a fresh pop's default seat puts the panel's bottom, measured from
   * the clip's own top (which rides the bar), not the window: a body that
   * grows after the pop (the picker replacing the roster) grows upward from
   * it, not down past the bar, and a taller window or a bar that moves
   * leaves it alone. Null once the user has placed the panel, and for a
   * remembered seat.
   */
  const growUp = useRef<number | null>(null);

  const popped = viewState === "popped";
  // Popped, the panel counters the bar's travel, so it stays where it was
  // left; only the chat moves it (ROUND7).
  const pin =
    floating && pinFrom !== null && barLeft !== undefined
      ? pinFrom - barLeft
      : 0;

  const go = useCallback(
    (next: CanvasTrayState, reason: CanvasTrayCloseReason) =>
      onState(next, reason),
    [onState],
  );

  // ---- the tool-switch cross-fade --------------------------------------
  // Keyed on the title, because the title IS the tool; a children change
  // inside one tool (picking a swatch) lands immediately. The in-flight fade
  // reads the newest payload from a ref rather than re-arming per render.
  const [view, setView] = useState({ title, children });
  const [swap, setSwap] = useState<"out" | null>(null);
  const pending = useRef({ title, children });
  pending.current = { title, children };

  useEffect(() => {
    if (title === view.title) {
      if (children !== view.children) setView((v) => ({ ...v, children }));
      if (swap) setSwap(null);
      return;
    }
    if (reduce || phase === "gone") {
      setView({ title, children });
      setSwap(null);
      return;
    }
    if (swap === "out") return;
    setSwap("out");
    swapTimer.current = setTimeout(() => {
      setView(pending.current);
      setSwap(null);
      if (wellRef.current) wellRef.current.scrollTop = 0;
    }, D_FAST);
  }, [title, children, view.title, view.children, reduce, phase, swap]);

  // ---- the state machine, rendered as motion --------------------------
  const prevState = useRef(current);
  useEffect(() => {
    const prev = prevState.current;
    if (prev === current) return;
    prevState.current = current;
    if (dockTimer.current) clearTimeout(dockTimer.current);

    if (current === "popped") {
      setFloating(true);
      setMoveDur(MOVE_LIFT);
      // The docked top-left, measured before the offset lands, is what the
      // seat is relative to; the clip then anchors at that top.
      const el = rootRef.current;
      const parent = el?.offsetParent as HTMLElement | null;
      if (el && parent) {
        const r = el.getBoundingClientRect();
        const p = parent.getBoundingClientRect();
        base.current = { left: r.left - p.left, top: r.top - p.top };
      }
      setAnchorTop(base.current.top);
      setPinFrom(barLeft ?? null);
      // The pop leaves the bar: a seat clear of it, or where this tray
      // was last left (ROUND3 §1), in canvas coordinates (ROUND7).
      const seat = memoryKey ? poppedSeats.get(memoryKey) : undefined;
      const stage = el?.closest<HTMLElement>(boundsSelector || "") ?? null;
      const c = canvasBox(stage);
      const r = el?.getBoundingClientRect();
      // Seats are saved against the canvas's top-left, or the origin when
      // nothing measurable holds the panel; read them back the same way.
      let next =
        seat && r
          ? {
              x: seat.left - (r.left - (c?.left ?? 0)),
              y: seat.top - (r.top - (c?.top ?? 0)),
            }
          : { x: TRAY_POP_DX, y: -(TRAY_TUCK + TRAY_POP_DY) };
      // The seat stays on the canvas and clear of the chat from the first
      // frame, as a drag keeps it.
      if (c && r) {
        next = {
          x: clamp(next.x, c.left - r.left, c.right - r.width - r.left),
          y: clamp(next.y, c.top - r.top, c.bottom - r.height - r.top),
        };
        // The chat's stand-off is the push, never the seat: when the chat
        // leaves, the panel returns to where it was left.
        const dx = chatPushX(
          {
            left: r.left + next.x,
            right: r.right + next.x,
            top: r.top + next.y,
            bottom: r.bottom + next.y,
          },
          beside ? null : chatBox(),
        );
        setPush(Math.max(dx, c.left - r.left - next.x));
      }
      growUp.current = !seat && r ? r.height + next.y : null;
      setOffset(next);
      setViewState("popped");
      setPhase("idle");
      return;
    }
    if (prev === "popped" && current !== "closed") {
      setMoveDur(MOVE_DOCK);
      // Travel to the docked seat under the top anchor, then hang from the
      // bar again once the travel has ended. The target needs the height the
      // clip will have docked, which only exists once the docked state has
      // rendered and the frame has re-measured: the layout pass below sets
      // it, before paint, so the dock is one motion (D56).
      dockPending.current = true;
      setOffset({ x: 0, y: 0 });
      setPinFrom(null);
      setPush(0);
      growUp.current = null;
      setViewState(current);
      setPhase("idle");
      dockTimer.current = setTimeout(() => {
        setFloating(false);
        setAnchorTop(null);
      }, D_SLOW);
      return;
    }
    if (current === "closed") {
      if (prev === "popped") {
        setMoveDur(MOVE_LIFT);
        setOffset((o) => ({ x: o.x, y: o.y + TRAY_TUCK }));
        setPhase("falling");
      } else {
        setPhase("leaving");
      }
      return;
    }
    if (prev === "closed") {
      setViewState(current);
      setPhase("entering");
      return;
    }
    setViewState(current);
    setPhase("idle");
  }, [current]);

  useEffect(() => {
    if (phase !== "entering" && phase !== "leaving" && phase !== "falling") {
      return undefined;
    }
    const ms = phase === "falling" ? D_MID : D_SLOW;
    if (phaseTimer.current) clearTimeout(phaseTimer.current);
    phaseTimer.current = setTimeout(
      () => setPhase(phase === "entering" ? "idle" : "gone"),
      ms,
    );
    return () => {
      if (phaseTimer.current) clearTimeout(phaseTimer.current);
    };
  }, [phase]);

  // Fully closed: the frame forgets where it floated; the seat memory
  // (`poppedSeats`) is what a re-pop returns to.
  useEffect(() => {
    if (phase !== "gone") return;
    setFloating(false);
    setAnchorTop(null);
    setOffset({ x: 0, y: 0 });
    setPinFrom(null);
    setPush(0);
    growUp.current = null;
    setMoveDur(MOVE_LIFT);
  }, [phase]);
  // The seat is remembered when a drag ends: the panel's top-left on the
  // canvas (ROUND7).
  const rememberSeat = useCallback(
    (o: { x: number; y: number }) => {
      const el = rootRef.current;
      if (!memoryKey || !el) return;
      const c = canvasBox(el.closest<HTMLElement>(boundsSelector || ""));
      const r = el.getBoundingClientRect();
      poppedSeats.set(memoryKey, {
        left: r.left - (c?.left ?? 0) + o.x,
        top: r.top - (c?.top ?? 0) + o.y,
      });
    },
    [memoryKey, boundsSelector],
  );

  // The chat holds a resting popped panel off itself: when it opens or
  // grows over the panel, the panel stands left by the overshoot; when it
  // leaves, the panel returns to where it was left (ROUND7).
  useEffect(() => {
    if (viewState !== "popped" || dragging || phase !== "idle") {
      return undefined;
    }
    const el = rootRef.current;
    if (!el) return undefined;
    const stage = el.closest<HTMLElement>(boundsSelector || "");
    let update = () => {
      const r = el.getBoundingClientRect();
      const c = canvasBox(stage);
      // The body can change size after the pop (the picker mounts in place
      // of the roster; a resize): a fresh default seat grows upward, and
      // every popped panel stays on the canvas. The seat moves; the effect
      // runs again with it.
      let { x, y } = offset;
      if (growUp.current !== null && r.height + y > growUp.current) {
        y = growUp.current - r.height;
      }
      if (c) {
        x = clamp(x, c.left - r.left, c.right - r.width - r.left);
        y = clamp(y, c.top - r.top, c.bottom - r.height - r.top);
      }
      if (x !== offset.x || y !== offset.y) {
        setOffset({ x, y });
        return;
      }
      const dx = chatPushX(
        {
          left: r.left + offset.x,
          right: r.right + offset.x,
          top: r.top + offset.y,
          bottom: r.bottom + offset.y,
        },
        beside ? null : chatBox(),
      );
      setPush(c ? Math.max(dx, c.left - r.left - offset.x) : dx);
    };
    const ro =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => update());
    // The chat itself is watched as well as the band: growing upward or
    // going full height changes no band width (ROUND7). It is re-found on
    // every pass, since the window and its button swap places.
    let watched: HTMLElement | null = null;
    const watchChat = () => {
      const next = chatEl();
      if (next === watched) return;
      if (watched) ro?.unobserve(watched);
      watched = next;
      if (next) ro?.observe(next);
    };
    const run = update;
    update = () => {
      watchChat();
      run();
    };
    update();
    if (stage) ro?.observe(stage);
    // The panel's own size: a body swap or a resize.
    ro?.observe(el);
    window.addEventListener("resize", update);
    // The chat's entrance settles after the band re-measures.
    const t = setTimeout(update, D_SLOW);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", update);
      clearTimeout(t);
    };
  }, [
    viewState,
    dragging,
    phase,
    offset.x,
    offset.y,
    boundsSelector,
    barLeft,
    beside,
  ]);

  useEffect(
    () => () => {
      if (phaseTimer.current) clearTimeout(phaseTimer.current);
      if (dockTimer.current) clearTimeout(dockTimer.current);
      if (swapTimer.current) clearTimeout(swapTimer.current);
    },
    [],
  );

  // ---- focus ----------------------------------------------------------
  // The user opened a picker to pick, so initial focus goes to the first
  // cell in the well — only when the content actually arrived (from
  // closed); docking back is not an open. Opened by a press while a
  // board editor holds the caret, the tray leaves it there: an emoji click
  // inserts at that caret (ROUND3 §3), and a keyboard open never arrives
  // with an editor focused.
  const openedFrom = useRef(current);
  useEffect(() => {
    const prev = openedFrom.current;
    openedFrom.current = current;
    const arrived = prev === "closed";
    if (current !== "open" || !arrived) return undefined;
    if (focusedEditableElement()) return undefined;
    const t = setTimeout(() => {
      const first = wellRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus?.({ preventScroll: true });
    }, 0);
    return () => clearTimeout(t);
  }, [current]);

  // ---- escape ---------------------------------------------------------
  // Open closes and hands focus back to the trigger (the bar's job). Popped
  // consumes Escape only when focus is inside it. The press is
  // not stopped: the placement hooks listen on window and disarm their tool
  // on the same Escape, which is what one press should do.
  // Backing out of a drilled-in body unmounts whatever held focus (the
  // picker, its Back control). Focus comes home to the first cell of the
  // body that replaced it, so a popped tray keeps answering Escape and a
  // keyboard user can still dismiss it.
  const hadHead = useRef(Boolean(head));
  useEffect(() => {
    const had = hadHead.current;
    hadHead.current = Boolean(head);
    if (!had || head || current === "closed") return undefined;
    const t = setTimeout(() => {
      const root = rootRef.current;
      if (!root || root.contains(document.activeElement)) return;
      const first =
        wellRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
        root.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus?.({ preventScroll: true });
    }, 0);
    return () => clearTimeout(t);
  }, [head, current]);

  const onBack = head?.onBack;
  useEffect(() => {
    if (current === "closed") return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const inside = rootRef.current?.contains(document.activeElement);
      if (current === "popped" && !inside) return;
      // A drilled-in body backs out first; the next Escape closes.
      if (onBack && inside) {
        e.stopPropagation();
        onBack();
        return;
      }
      go("closed", "escape");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [current, go, onBack]);

  // ---- click-outside --------------------------------------------------
  // Only the docked-open tray is dismissed by the canvas: popped must not
  // close, because surviving canvas interaction is the point of popping out.
  useEffect(() => {
    if (current !== "open") return undefined;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || rootRef.current?.contains(target)) return;
      if (ignoreSelector && target.closest?.(ignoreSelector)) return;
      go("closed", "outside");
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [current, ignoreSelector, go]);

  // ---- the things the frame measures -----------------------------------
  const floatingRef = useRef(floating);
  floatingRef.current = floating;
  const measure = useCallback(() => {
    const w = wellRef.current;
    if (!w) return;
    const clipEl = w.closest<HTMLElement>(".xtt-clip");
    if (clipEl) {
      const stage = boundsSelector
        ? clipEl.closest<HTMLElement>(boundsSelector)
        : null;
      // Docked, the room is the gap between the drawer's own pinned bottom
      // edge and the top of the box it lives in; popped, the canvas the
      // panel roams (a top-anchored clip's bottom follows its content).
      // Either less the chrome, its gap and the pop-out lift. Docked beside
      // the column, the clip also stands no taller than the column.
      const room = floatingRef.current ? canvasBox(stage) : null;
      const bottom = room ? room.bottom : clipEl.getBoundingClientRect().bottom;
      const top = room
        ? room.top
        : stage
          ? stage.getBoundingClientRect().top
          : 0;
      const column =
        beside && !floatingRef.current
          ? (clipEl.offsetParent as HTMLElement | null)?.offsetHeight
          : undefined;
      clipEl.style.setProperty(
        "--xtt-avail",
        `${deckAvail({ room: bottom - top, column, beside })}px`,
      );
      // Docked, the clip never crosses the chat (D54); popped, the drag's
      // own clamp is enough.
      const mount = clipEl.offsetParent as HTMLElement | null;
      if (stage && mount && !floatingRef.current && !beside) {
        const m = mount.getBoundingClientRect();
        const s = stage.getBoundingClientRect();
        clipEl.style.setProperty(
          "--xtt-dock-x",
          `${Math.round(
            dockedClipShift({
              centre: m.left + m.width / 2,
              halfWidth: clipEl.offsetWidth / 2,
              boundsLeft: s.left,
              boundsRight: s.right,
              inset: TRAY_DOCK_INSET,
            }),
          )}px`,
        );
      } else {
        clipEl.style.removeProperty("--xtt-dock-x");
      }
    }
    const tray = w.querySelector<HTMLElement>(".xtc");
    if (tray) {
      for (const [sel, prop] of [
        [":scope > .xtc-head", "--xtc-head-h"],
        [":scope > .xtc-foot", "--xtc-foot-h"],
      ] as const) {
        const el = tray.querySelector<HTMLElement>(sel);
        const h = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
        if (h > 0) tray.style.setProperty(prop, `${h}px`);
        else tray.style.removeProperty(prop);
      }
    }
    const swapEl = w.firstElementChild as HTMLElement | null;
    if (swapEl) {
      const h = Math.ceil(swapEl.scrollHeight) + 1;
      if (clipEl && h > 1) clipEl.style.setProperty("--xtt-h", `${h}px`);
    }
    const scroller = w.querySelector<HTMLElement>(".xtc-deck");
    if (!scroller) return;
    const scrollable = scroller.scrollHeight - scroller.clientHeight > 1;
    const atEnd =
      scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
    if (scrollable && !atEnd) scroller.dataset.fade = "true";
    else delete scroller.dataset.fade;
  }, [boundsSelector, beside]);

  useLayoutEffect(() => {
    measure();
    const el = rootRef.current;
    if (dockPending.current && el && viewState !== "popped") {
      dockPending.current = false;
      const mount = el.offsetParent as HTMLElement | null;
      setAnchorTop(
        beside && mount
          ? mount.offsetHeight - el.offsetHeight
          : dockedClipTop(el.offsetHeight, wide),
      );
    }
  }, [
    measure,
    view.children,
    viewState,
    phase,
    floating,
    reflowKey,
    wide,
    beside,
  ]);

  // The bar slides to a new left over the row's 200ms (D53), and the dock
  // shift above was measured at the start of that slide. Measure again
  // once the slide has landed, so a docked tray re-centres on the bar
  // without waiting for some later render (ROUND7).
  useEffect(() => {
    if (reflowKey === undefined) return undefined;
    const t = setTimeout(measure, ROW_SLIDE_SETTLE_MS);
    return () => clearTimeout(t);
  }, [reflowKey, measure]);

  useEffect(() => {
    const w = wellRef.current;
    if (!w) return undefined;
    const scroller = w.querySelector<HTMLElement>(".xtc-deck") || w;
    scroller.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(w);
      if (w.firstElementChild) ro.observe(w.firstElementChild);
      w.querySelectorAll(".xtc-deck, .xtc-head, .xtc-foot").forEach((el) =>
        ro?.observe(el),
      );
    }
    return () => {
      scroller.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [measure, view.children]);

  // ---- drag -----------------------------------------------------------
  // Popped, the WHOLE panel is the handle (ROUND3 §1, a departure from the
  // sheet's chrome-row handle): a pointer that moves past the threshold
  // carries it, 1:1, no animation, the clip clamped to the stage; one that
  // does not is the click it always was, so tiles, plates, swatches, rows
  // and the search field keep working. Anything that starts a drag of its
  // own wins (ROUND5 §5): text fields, draggable plates and the picker's
  // dnd-kit rows never start a panel drag.
  // The press lives on the window, not the clip: the chrome row is 24 tall,
  // so a quick grab leaves the clip before its first move, and a release
  // the clip never hears would leave the press armed for the next hover to
  // carry. Every way a press can end ends the drag.
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const unlisten = useRef<(() => void) | null>(null);

  const endDrag = useCallback(() => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    unlisten.current?.();
    unlisten.current = null;
    if (!d.carrying) return;
    setDragging(false);
    rememberSeat(offsetRef.current);
    try {
      const el = rootRef.current;
      if (el?.hasPointerCapture?.(d.pointerId)) {
        el.releasePointerCapture(d.pointerId);
      }
    } catch {
      // already released
    }
  }, [rememberSeat]);

  const moveDrag = useCallback(
    (e: PointerEvent) => {
      const d = drag.current;
      if (!d || e.pointerId !== d.pointerId) return;
      // No button down: the release went somewhere nothing heard it.
      if (e.buttons === 0) {
        endDrag();
        return;
      }
      const dx = e.clientX - d.px;
      const dy = e.clientY - d.py;
      if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
      if (!d.carrying) {
        if (Math.hypot(dx, dy) < TRAY_DRAG_THRESHOLD) return;
        d.carrying = true;
        try {
          rootRef.current?.setPointerCapture?.(d.pointerId);
        } catch {
          // a pointer the browser no longer tracks; the window still hears it
        }
        setPush(0);
        growUp.current = null;
        setDragging(true);
      }
      e.preventDefault();
      let x = clamp(d.ox + dx, d.minX, d.maxX);
      const y = clamp(d.oy + dy, d.minY, d.maxY);
      // Free above and below the chat; beside it, the panel slides along its
      // edge instead of passing under it (ROUND7).
      x = Math.max(
        x +
          chatPushX(
            {
              left: d.rect.left + x,
              right: d.rect.right + x,
              top: d.rect.top + y,
              bottom: d.rect.bottom + y,
            },
            d.chat,
          ),
        d.minX,
      );
      setOffset({ x, y });
    },
    [endDrag],
  );

  // Keyed on the state asked for, not viewState: a close keeps viewState at
  // its last open state for the exit, and the press must end with it.
  useEffect(() => {
    if (current !== "popped") endDrag();
  }, [current, endDrag]);
  // Unmounting finishes the press too. A layout cleanup runs before the
  // clip's ref detaches, so the seat is still remembered.
  const endDragRef = useRef(endDrag);
  endDragRef.current = endDrag;
  useLayoutEffect(() => () => endDragRef.current(), []);

  const onHandleDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (viewState !== "popped") return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest?.(PANEL_DRAG_EXCLUDED)) return;
    const el = rootRef.current;
    if (!el) return;
    endDrag();
    const rect = el.getBoundingClientRect();
    const stage = boundsSelector
      ? el.closest<HTMLElement>(boundsSelector)
      : null;
    // The whole canvas, not the band (ROUND7); the viewport without one.
    const b = canvasBox(stage) ?? {
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
    };
    drag.current = {
      pointerId: e.pointerId,
      px: e.clientX,
      py: e.clientY,
      // A panel the chat is holding is carried from where it stands.
      ox: offset.x + push,
      oy: offset.y,
      minX: b.left - rect.left,
      maxX: b.right - rect.width - rect.left,
      minY: b.top - rect.top,
      maxY: b.bottom - rect.height - rect.top,
      rect,
      chat: beside ? null : chatBox(),
      carrying: false,
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId === drag.current?.pointerId) endDrag();
    };
    const onHidden = () => {
      if (document.visibilityState === "hidden") endDrag();
    };
    window.addEventListener("pointermove", moveDrag);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("blur", endDrag);
    document.addEventListener("visibilitychange", onHidden);
    unlisten.current = () => {
      window.removeEventListener("pointermove", moveDrag);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      window.removeEventListener("blur", endDrag);
      document.removeEventListener("visibilitychange", onHidden);
    };
    // No preventDefault and no capture yet: below the threshold the press
    // must stay a click, with focus landing where it landed.
  };

  if (phase === "gone" && current === "closed") return null;

  // Two verbs: Pop out (Dock while popped) and Close, always last. The
  // sheet's Minimize is gone (ROUND5 §4).
  const acts = (
    <div className="xtt-acts">
      <button
        type="button"
        className="xtt-btn"
        data-act="pop"
        data-on={popped ? "true" : undefined}
        aria-label={popped ? "Dock" : "Pop out"}
        onClick={() => go(popped ? "open" : "popped", "chrome")}
      >
        <PictureInPicture2
          size={CANVAS_MENU_GLYPH}
          strokeWidth={CANVAS_GLYPH_STROKE}
          aria-hidden="true"
        />
      </button>
      <button
        type="button"
        className="xtt-btn"
        data-act="close"
        aria-label="Close"
        onClick={() => go("closed", "chrome")}
      >
        <X
          size={CANVAS_MENU_GLYPH}
          strokeWidth={CANVAS_GLYPH_STROKE}
          aria-hidden="true"
        />
      </button>
    </div>
  );

  // Popped, the clip is the non-modal dialog: Dock and Close stand outside
  // the panel, so the role sits on the box that contains them.
  return (
    <div
      ref={rootRef}
      className="xtt-clip"
      data-canvas-control
      data-state={viewState}
      data-phase={phase}
      data-swap={swap || undefined}
      data-dragging={dragging ? "true" : undefined}
      data-float={floating ? "true" : undefined}
      data-wide={wide ? "true" : undefined}
      data-beside={beside ? "true" : undefined}
      data-portrait={beside ? "true" : undefined}
      data-bar-still={barEases ? undefined : "true"}
      role={popped ? "dialog" : undefined}
      aria-modal={popped ? "false" : undefined}
      aria-labelledby={popped ? titleId : undefined}
      // The canvas wrapper closes its menus on any click that reaches it; a
      // pick inside the drawer is not that click.
      onClick={(e) => e.stopPropagation()}
      onPointerDown={onHandleDown}
      onLostPointerCapture={endDrag}
      style={
        {
          "--xtt-x": `${offset.x + push}px`,
          "--xtt-y": `${offset.y}px`,
          "--xtt-pin": `${pin}px`,
          "--xtt-move-d": moveDur,
          ...(anchorTop === null
            ? {}
            : { top: `${anchorTop}px`, bottom: "auto" }),
        } as React.CSSProperties
      }
    >
      <div className="xtt-chrome" data-head={head ? "true" : undefined}>
        {popped ? (
          // The row reads as the handle while popped (ROUND5 §5).
          <span className="xtt-grip" aria-hidden="true">
            <GripHorizontal
              size={CANVAS_MENU_GLYPH}
              strokeWidth={CANVAS_GLYPH_STROKE}
            />
          </span>
        ) : null}
        {head ? (
          <>
            <button
              type="button"
              className="xtt-btn"
              data-act="back"
              aria-label={head.backLabel}
              onClick={head.onBack}
            >
              <ChevronLeft
                size={CANVAS_MENU_GLYPH}
                strokeWidth={CANVAS_GLYPH_STROKE}
                aria-hidden="true"
              />
            </button>
            <span className="xtt-head" id={titleId}>
              <span
                className="xtt-head-lead"
                aria-hidden="true"
                // The brand mark is an image, which the browser drags on its
                // own and so stops the panel's drag; the chrome is the handle.
                onDragStart={(e) => e.preventDefault()}
              >
                {head.lead}
              </span>
              <span className="xtt-head-label">{head.label}</span>
            </span>
          </>
        ) : (
          <span className="xtt-title" id={titleId}>
            {view.title}
          </span>
        )}
        {acts}
      </div>

      <div className="xtt" id={regionId}>
        <div
          ref={wellRef}
          className="xtt-well"
          role="group"
          aria-labelledby={titleId}
        >
          <div className="xtt-swap">
            <TrayPortraitProvider value={beside}>
              {view.children}
            </TrayPortraitProvider>
          </div>
        </div>
      </div>
    </div>
  );
}
